import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { Album } from '../../../models/album.model';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { Group } from '../../../models/group.model';
import { UserModel } from '../../../models/user.model';
import { UtilsService } from '../../../services/utils.service';
import * as albumActions from '../../../state/actions/album.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';

@Component({
  selector: 'dlg-edit-album',
  standalone: true,
  imports: [BtnActionComponent, AsyncPipe, ReactiveFormsModule, FormsModule],
  templateUrl: './dlg-edit-album.component.html',
  styleUrl: './dlg-edit-album.component.css',
})
export class DlgEditAlbumComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgEditAlbumComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  private actions$: Actions = inject(Actions);
  utilsService: UtilsService = inject(UtilsService);
  store: Store<AppState> = inject(Store<AppState>);

  group: Group | null | undefined = undefined;
  user: UserModel | null = null;

  working$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();

  pageForm: FormGroup = new FormGroup({});

  constructor() {
    this.pageForm = new FormGroup({
      albumName: new FormControl(this.data.album?.albumName || '', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      description: new FormControl(this.data.album?.description || '', [
        Validators.maxLength(3000),
      ]),
      albumDate: new FormControl(
        this.data.album?.albumDate
          ? dayjs(this.data.album.albumDate.toDate()).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        [Validators.required],
      ),
    });
  }
  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.actions$
      .pipe(ofType(albumActions.updateFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.logger.error('action=updateFailure', action.error);
        this.utilsService.onShowAlert(action.error as AppExceptionModel, false);
        this.working$ = of(false);
      });

    this.actions$
      .pipe(ofType(albumActions.updateSuccess), takeUntil(this.exit$))
      .subscribe((action) => {
        this.toastr.success('Album updated successfully', 'Update Successful');
        this.dialogRef.close(action.album);
        this.working$ = of(false);
      });
  }

  onCancel() {
    this.exit$.complete();
    this.exit$.next();
    this.dialogRef.close();
  }

  submit() {
    if (this.group == null || this.user == null) {
      this.utilsService.onShowAlert(
        {
          title: 'Error Updating Album',
          message: 'Unable to update album. Missing required information.',
        },
        false,
      );
      this.working$ = of(false);
      return;
    }
    this.working$ = of(true);
    const album: Partial<Album> = {
      ...this.data.album,
      albumName: this.pageForm.get('albumName')?.value,
      description: this.pageForm.get('description')?.value,
      albumDate: Timestamp.fromDate(
        dayjs(this.pageForm.get('albumDate')?.value).toDate(),
      ),
    };

    delete album.id;
    delete album.created;
    delete album.modified;

    this.store.dispatch(
      albumActions.updateAlbum({
        id: this.data.album.id,
        album: album,
      }),
    );
  }
}
